import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import { getAllBatchNameNoPagination } from '../../redux/features/batchSlice';
import {
  getSkillxBatchBadgeAwards,
  getSkillxCommonBadges,
} from '../../redux/features/skillxSlice';
import AwardBadgeDialog from './badges/AwardBadgeDialog';
import RevokeBadgeDialog from './badges/RevokeBadgeDialog';
import { SX } from './leaderboard/leaderboardData';

/**
 * Recognition badges — the mentor's page.
 *
 * SkillX badges come in two tiers, and only one of them is a person's decision.
 *
 *   MODULE BADGES (15, one per project) are earned automatically: approve the
 *   project's submission in the assignments area and the badge follows. They
 *   are derived from that approval, never stored, and there is nothing to do
 *   here for them — which is why this page does not list them at all.
 *
 *   COMMON BADGES are cross-cutting recognition — Perfect Attendance, Community
 *   Helper, Presentation Star. Nothing in the system observes those, so a
 *   mentor awards them by hand. That is this page.
 *
 * The two are told apart by `achievement_badges.assignment_id`: set means
 * module, NULL means common. Only the NULL ones are offered here, and the
 * server filters the same way, so a module badge cannot be hand-awarded even by
 * posting its id.
 *
 * ORGANISED BY BATCH, THEN BY LEARNER — the same shape as the Award XP page,
 * because recognition is something you give while looking at a group. "Who in
 * this batch has been carrying the sessions" is the question a mentor actually
 * arrives with, and it is not answerable from a flat list of awards.
 *
 * WHY THE ROSTER IS FILTERED CLIENT-SIDE, unlike the XP audit page next door.
 * That page pages a ledger that grows without bound; this one holds a single
 * batch — thirty-odd learners, already in memory. Round-tripping to the server
 * to hide a few rows would be slower and no more correct.
 *
 * A learner can hold the same badge more than once and the count is meaningful,
 * so repeats show as ×N. Revoking still acts on one award at a time — see
 * RevokeBadgeDialog, which asks which one when there is a choice to make.
 */

/** Stable empty list — see the note where the roster is read. */
const EMPTY = [];

const filterBatchOptions = createFilterOptions({
  limit: 100,
  stringify: (option) => option.course_batch_name || '',
});

/** One header statistic. Mirrors the Award XP page so the two read as one area. */
function StatCard({ icon, value, label, color }) {
  return (
    <Box
      sx={{
        p: 2,
        height: '100%',
        borderRadius: `${SX.radius.md}px`,
        background: SX.card,
        border: `1px solid ${SX.borderStrong}`,
        boxShadow: SX.shadowSoft,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Box
          sx={{
            width: 38,
            height: 38,
            flexShrink: 0,
            borderRadius: `${SX.radius.sm}px`,
            background: `${color}14`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon icon={icon} width={20} height={20} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.1, color: SX.text }}>
            {value}
          </Typography>
          <Typography noWrap sx={{ fontSize: 11.5, color: SX.textMuted }}>
            {label}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

/** One learner row: who they are, what they hold, and a way to add to it. */
function LearnerRow({ learner, batch, onChanged }) {
  // Repeats collapse into one chip with a count — three Perfect Attendance
  // awards is a fact about the learner, not three things to render. The
  // individual awards are kept on `items` because revoking acts on one row and
  // the dialog has to be able to name them.
  const grouped = useMemo(() => {
    const byBadge = new Map();
    (learner.awards || []).forEach((a) => {
      const seen = byBadge.get(a.badgeId);
      if (seen) seen.items.push(a);
      else byBadge.set(a.badgeId, { badgeId: a.badgeId, badgeName: a.badgeName, items: [a] });
    });
    return [...byBadge.values()];
  }, [learner]);

  return (
    <Box sx={{ py: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <Avatar src={learner.photo || undefined} sx={{ width: 38, height: 38 }}>
            {(learner.name || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{learner.name}</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Adm no {learner.admnNo}
            </Typography>
          </Box>
        </Stack>

        <AwardBadgeDialog batch={batch} learner={learner} onAwarded={onChanged} />
      </Stack>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
        {grouped.length ? (
          grouped.map((group) => {
            const latest = group.items[0];
            return (
              <Tooltip
                key={group.badgeId}
                title={
                  latest?.note
                    ? `${latest.note}${latest.awardedBy ? ` — ${latest.awardedBy}` : ''}`
                    : 'No reason recorded'
                }
              >
                <Chip
                  size="small"
                  icon={<Icon icon="solar:cup-star-bold-duotone" width={15} height={15} />}
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>{group.badgeName}</span>
                      {group.items.length > 1 && <b>×{group.items.length}</b>}
                      <RevokeBadgeDialog
                        badgeName={group.badgeName}
                        awards={group.items}
                        learner={learner}
                        onRevoked={onChanged}
                      />
                    </Stack>
                  }
                  sx={{ fontWeight: 600 }}
                />
              </Tooltip>
            );
          })
        ) : (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            No recognition badges yet.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export default function SkillxCommonBadges() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { batchesAll } = useSelector((state) => state.batch);
  const { commonBadges } = useSelector((state) => state.skillx);

  const [batchId, setBatchId] = useState('');
  const [search, setSearch] = useState('');
  const [recognition, setRecognition] = useState('ALL');

  useEffect(() => {
    dispatch(getAllBatchNameNoPagination({ isAllBatchesNeeded: true }));
    dispatch(getSkillxCommonBadges());
  }, [dispatch]);

  useEffect(() => {
    if (batchId) dispatch(getSkillxBatchBadgeAwards(batchId));
  }, [dispatch, batchId]);

  const selectedBatch = useMemo(
    () => (batchesAll || []).find((b) => b.course_batch_id === batchId) || null,
    [batchesAll, batchId],
  );

  const refresh = () => batchId && dispatch(getSkillxBatchBadgeAwards(batchId));

  // EMPTY rather than a fresh `[]`: the roster feeds a useMemo, and a new array
  // literal on every render would rebuild the filtered list every time.
  const catalogue = commonBadges?.catalogue || EMPTY;
  const roster = commonBadges?.roster || EMPTY;

  // Counted over the whole batch, not the filtered view: "12 awarded" should
  // not drop to 1 because someone typed a name into the search box.
  const awardedCount = roster.reduce((sum, l) => sum + (l.awards?.length || 0), 0);
  const recognisedCount = roster.filter((l) => (l.awards?.length || 0) > 0).length;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roster.filter((l) => {
      const held = l.awards?.length || 0;
      if (recognition === 'RECOGNISED' && !held) return false;
      if (recognition === 'NONE' && held) return false;
      if (!q) return true;
      return (
        (l.name || '').toLowerCase().includes(q) || String(l.admnNo || '').includes(q)
      );
    });
  }, [roster, search, recognition]);

  // Both pages are siblings under the same router, so swapping the last segment
  // reaches the badge CRUD without hard-coding the workspace prefix.
  const goToCatalogue = () =>
    navigate(location.pathname.replace(/[^/]+$/, 'achievement-badges'));

  return (
    <GlobalWrapper title="SkillX — Recognition Badges">
      {/* ---------- what this batch has been given ---------- */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:users-group-rounded-bold-duotone"
            value={batchId ? roster.length : '—'}
            label="Learners on this batch"
            color={SX.blue}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:cup-star-bold-duotone"
            value={batchId ? awardedCount : '—'}
            label="Badges awarded"
            color="#C25E00"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:medal-ribbon-bold-duotone"
            value={batchId ? recognisedCount : '—'}
            label="Learners recognised"
            color="#2E7D62"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:widget-add-bold-duotone"
            value={catalogue.length}
            label="Badges available to give"
            color={SX.purple}
          />
        </Grid>
      </Grid>

      {/* The page is unusable without a catalogue, and the reason is not
          obvious — the fifteen badges that DO exist are all module badges and
          are deliberately hidden here. Say so, and point at the fix. */}
      {!catalogue.length && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Typography
              onClick={goToCatalogue}
              sx={{
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 700,
                color: SX.blue,
                whiteSpace: 'nowrap',
              }}
            >
              Manage badges
            </Typography>
          }
        >
          <AlertTitle sx={{ fontSize: 14 }}>Nothing to award yet</AlertTitle>
          <Typography sx={{ fontSize: 13 }}>
            Every badge defined so far is tied to a project, and those are earned
            automatically when you approve the submission. To create one you can give by
            hand, add a badge on the Achievement Badges page and leave its assignment
            blank.
          </Typography>
        </Alert>
      )}

      {/* ---------- pick a batch, then narrow it ---------- */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: `${SX.radius.md}px`,
          background: SX.card,
          border: `1px solid ${SX.borderStrong}`,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Autocomplete
              fullWidth
              size="small"
              options={batchesAll || []}
              filterOptions={filterBatchOptions}
              getOptionLabel={(option) => option.course_batch_name || ''}
              isOptionEqualToValue={(option, value) =>
                option.course_batch_id === value?.course_batch_id
              }
              value={selectedBatch}
              onChange={(e, option) => setBatchId(option?.course_batch_id ?? '')}
              renderInput={(params) => (
                <TextField {...params} label="Batch" autoComplete="off" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={7} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Find a learner"
              placeholder="Name or admission number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={!batchId}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="solar:magnifer-linear" width={17} height={17} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={5} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Show"
              value={recognition}
              onChange={(e) => setRecognition(e.target.value)}
              disabled={!batchId}
            >
              <MenuItem value="ALL">Everyone</MenuItem>
              <MenuItem value="RECOGNISED">Recognised already</MenuItem>
              <MenuItem value="NONE">Not yet recognised</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          p: 2,
          borderRadius: `${SX.radius.md}px`,
          background: SX.card,
          border: `1px solid ${SX.borderStrong}`,
          boxShadow: SX.shadowSoft,
        }}
      >
        {!batchId ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
            <Icon icon="solar:cup-star-line-duotone" width={48} height={48} />
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
              Pick a batch to get started
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                color: 'text.secondary',
                textAlign: 'center',
                maxWidth: 460,
              }}
            >
              Recognition badges are given by hand for attendance, helping classmates and
              standout presentations. Module badges are awarded automatically when you
              approve a project, so they are not listed here.
            </Typography>
          </Stack>
        ) : commonBadges?.loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : !roster.length ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              No active learners on this batch.
            </Typography>
          </Stack>
        ) : !visible.length ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              No learner on this batch matches those filters.
            </Typography>
          </Stack>
        ) : (
          <Box>
            {visible.map((learner, i) => (
              <React.Fragment key={learner.admnNo}>
                {i > 0 && <Divider />}
                <LearnerRow learner={learner} batch={selectedBatch} onChanged={refresh} />
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>
    </GlobalWrapper>
  );
}
